import React, { useEffect, useState } from 'react';
import { useAdminTheme } from '../context/AdminThemeContext';
import { Upload, Trash2, FileText, Loader2 } from 'lucide-react';
import Notification from '../../Component/Notification';
import { useAuth } from '../../context/AuthContext'; // Import Auth

const AdminNotes: React.FC = () => {
  const { colors } = useAdminTheme();
  const { token } = useAuth(); // Get Token
  const [activeTab, setActiveTab] = useState<'upload' | 'list'>('list');
  const [notes, setNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Upload Form State
  const [formData, setFormData] = useState({ title: '', subject: '', summary: '' });
  const [file, setFile] = useState<File | null>(null);

  const [notify, setNotify] = useState<{ isOpen: boolean; message: string; type: 'success' | 'error' }>({
    isOpen: false, message: '', type: 'success'
  });

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    try {
      // Fetching notes is public (usually), but if protected, add token header here too
    //   const res = await fetch('http://localhost:2025/api/notes');
      const res = await fetch('https://www.projectinfotech.in/api/notes');
      if (res.ok) setNotes(await res.json());
    } catch (err) { console.error(err); }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return alert("Select a file");
    
    // FIX: FormData generation
    const data = new FormData();
    data.append('file', file);
    data.append('title', formData.title);
    data.append('subject', formData.subject);
    data.append('summary', formData.summary);

    setUploading(true);
    try {
      // FIX: Added Authorization Header
    //   const res = await fetch('http://localhost:2025/api/notes/upload', { 
        const res = await fetch('https://www.projectinfotech.in/api/notes/upload', { 
          method: 'POST', 
          headers: {
            'Authorization': `Bearer ${token}` // This fixes the CORS/Redirect issue
          },
          body: data 
      });

      if (res.ok) {
        setNotify({ isOpen: true, message: 'Note uploaded successfully!', type: 'success' });
        setFormData({ title: '', subject: '', summary: '' });
        setFile(null);
        fetchNotes();
        setActiveTab('list');
      } else {
        setNotify({ isOpen: true, message: 'Upload failed. Check server logs.', type: 'error' });
      }
    } catch (err) {
        setNotify({ isOpen: true, message: 'Server error or Connection refused', type: 'error' });
    } finally {
        setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if(!window.confirm("Delete note?")) return;
    
    try {
        // await fetch(`http://localhost:2025/api/notes/${id}`, { 
        await fetch(`https://www.projectinfotech.in/api/notes/${id}`, { 
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}` // Authorization needed for delete too
            }
        });
        setNotes(notes.filter(n => n.id !== id));
        setNotify({ isOpen: true, message: 'Note deleted', type: 'success' });
    } catch (error) {
        setNotify({ isOpen: true, message: 'Failed to delete', type: 'error' });
    }
  };

  return (
    <div className="animate-fade-in-up">
      <Notification isOpen={notify.isOpen} message={notify.message} type={notify.type} onClose={() => setNotify({ ...notify, isOpen: false })} />
      
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-2xl font-bold ${colors.navbarText}`}>Notes Manager</h2>
        <div className="flex gap-2">
            <button onClick={() => setActiveTab('list')} className={`px-4 py-2 rounded-lg font-bold transition-all ${activeTab === 'list' ? colors.button3D + ' text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>List</button>
            <button onClick={() => setActiveTab('upload')} className={`px-4 py-2 rounded-lg font-bold transition-all ${activeTab === 'upload' ? colors.button3D + ' text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>Upload</button>
        </div>
      </div>

      <div className={`p-6 rounded-xl shadow-sm border ${colors.cardBg} ${colors.borderColor}`}>
        
        {activeTab === 'upload' ? (
            <form onSubmit={handleUpload} className="max-w-lg mx-auto space-y-4">
                <div>
                    <label className="block text-sm font-bold mb-1 opacity-70">Note Title</label>
                    <input type="text" placeholder="e.g. Java Basics Chapter 1" required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className={`w-full p-3 border rounded-lg ${colors.inputBg} ${colors.borderColor} focus:ring-2 focus:ring-red-500 outline-none`} />
                </div>
                
                <div>
                    <label className="block text-sm font-bold mb-1 opacity-70">Subject / Category</label>
                    <select required value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} className={`w-full p-3 border rounded-lg ${colors.inputBg} ${colors.borderColor} focus:ring-2 focus:ring-red-500 outline-none`}>
                        <option value="">Select Subject</option>
                        <option value="Java">Java</option>
                        <option value="Python">Python</option>
                        <option value="MERN Stack">MERN Stack</option>
                        <option value="Data Science">Data Science</option>
                        <option value="AWS/DevOps">AWS/DevOps</option>
                        <option value="General">General</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-bold mb-1 opacity-70">Summary</label>
                    <textarea placeholder="Short description..." required value={formData.summary} onChange={e => setFormData({...formData, summary: e.target.value})} className={`w-full p-3 border rounded-lg ${colors.inputBg} ${colors.borderColor} focus:ring-2 focus:ring-red-500 outline-none`} rows={3} />
                </div>

                <div className="border-2 border-dashed border-gray-300 p-6 rounded-lg text-center cursor-pointer hover:bg-gray-50 transition">
                    <input type="file" accept=".pdf" onChange={e => setFile(e.target.files ? e.target.files[0] : null)} className="w-full" required />
                    <p className="text-xs text-gray-500 mt-2">Only PDF files allowed</p>
                </div>

                <button type="submit" disabled={uploading} className={`w-full py-3 rounded-lg text-white font-bold flex items-center justify-center gap-2 ${colors.button3D} ${uploading ? 'opacity-70 cursor-not-allowed' : ''}`}>
                    {uploading ? <Loader2 className="animate-spin" /> : <Upload size={20} />}
                    {uploading ? "Uploading..." : "Upload Note"}
                </button>
            </form>
        ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {notes.length === 0 ? <p className="col-span-3 text-center py-10 opacity-50">No notes uploaded yet.</p> : notes.map(note => (
                    <div key={note.id} className={`p-4 border rounded-xl flex flex-col justify-between min-h-[160px] relative group hover:shadow-lg transition ${colors.cardBg} ${colors.borderColor}`}>
                        <div>
                            <div className="flex justify-between items-start">
                                <div className="p-2 bg-red-50 rounded-lg text-red-600 mb-3">
                                    <FileText size={24} />
                                </div>
                                <span className="text-[10px] uppercase font-bold tracking-wider bg-gray-100 px-2 py-1 rounded text-gray-600">{note.subject}</span>
                            </div>
                            <h4 className={`font-bold text-lg leading-tight mb-1 ${colors.cardText}`}>{note.title}</h4>
                            <p className="text-xs opacity-60 line-clamp-2">{note.summary}</p>
                        </div>
                        <div className="flex justify-between items-end mt-4 pt-4 border-t border-gray-100">
                            <span className="text-xs font-mono opacity-50">{note.date}</span>
                            <button onClick={() => handleDelete(note.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition" title="Delete Note"><Trash2 size={18}/></button>
                        </div>
                    </div>
                ))}
            </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotes;